import * as React from 'react'
// hooks
import { useFormsLinking } from 'hooks'
import { useNetworkStatus } from 'hooks/useNetworkStatus'
// context
import { RiskViewAPIContext } from 'containers/Risks/RiskView/layers/API'
// views
import {
  Button,
  EditIcon,
  TrashIcon,
  ButtonsWrapper
} from 'components/PageComponents/Settings/ObjectsAndProperties/Edit/Properties/components/Property/view'
import { CopyIcon, LinkIcon, NewTabIcon, VisibilityIcon } from './views'
// components
import Tooltip from '@mui/material/Tooltip'
// types
import { IRisk } from 'types/graphql'

interface Props {
  risk: IRisk
  canView: boolean
  canEdit: boolean
  canDelete: boolean
  isImported: boolean
  onEditClick: any
  onDeleteClick: any
}

const RiskControlButtons: React.FC<Props> = ({
  risk,
  canView,
  canEdit,
  canDelete,
  isImported,
  onEditClick,
  onDeleteClick
}) => {
  const { risksToLink, setRisksToLink } = React.useContext(RiskViewAPIContext)

  const onLine = useNetworkStatus()

  const { isLinking, isFormView, linkingSettings } = useFormsLinking()

  const openRisk = () => onEditClick()

  const openRiskInNewTab = () => onEditClick(true)

  if (isLinking) {
    const onButtonClick = (action: 'copy' | 'reference') => () => {
      setRisksToLink((prev) => {
        const riskToLink = prev.find((item) => item.id === risk.id)

        if (riskToLink) {
          if (riskToLink.action === action) {
            return prev.filter((item) => item.id !== risk.id)
          } else {
            return prev.map((item) => {
              if (item.id === risk.id) {
                return { ...item, action: item.action === action ? null : action }
              }
              return item
            })
          }
        }

        return [...prev, { ...risk, action }]
      })
    }

    const currentAction = risksToLink.find((item) => item.id === risk.id)?.action

    return (
      <ButtonsWrapper>
        {linkingSettings.copyRecords && (
          <Button data-testid='link-risk-copy' data-entity-id={risk.id} onClick={onButtonClick('copy')}>
            <Tooltip title='Copy' placement='top-start'>
              <CopyIcon active={currentAction === 'copy'} />
            </Tooltip>
          </Button>
        )}
        {linkingSettings.referenceRecords && (
          <Button data-testid='link-risk-reference' data-entity-id={risk.id} onClick={onButtonClick('reference')}>
            <Tooltip title='Reference' placement='top-start'>
              <LinkIcon active={currentAction === 'reference'} />
            </Tooltip>
          </Button>
        )}
      </ButtonsWrapper>
    )
  }

  if (isFormView) {
    return null
  }

  return (
    <ButtonsWrapper>
      {!isImported && (
        <Button data-testid='open-risk' data-entity-id={risk.id} disabled={onLine && !canEdit} onClick={openRisk}>
          <Tooltip title='Edit' placement='bottom-start'>
            <EditIcon />
          </Tooltip>
        </Button>
      )}
      {onLine && (!canEdit || isImported) && (
        <Button data-testid='open-risk' data-entity-id={risk.id} onClick={openRisk}>
          <Tooltip title='View' placement='bottom-start'>
            <VisibilityIcon />
          </Tooltip>
        </Button>
      )}
      {onLine && canView && (
        <Button data-testid='open-risk-new-tab' data-entity-id={risk.id} onClick={openRiskInNewTab}>
          <Tooltip title='Open in new tab' placement='bottom-start'>
            <NewTabIcon />
          </Tooltip>
        </Button>
      )}
      <Button data-entity-id={risk.id} onClick={onDeleteClick} disabled={onLine && !canDelete}>
        <Tooltip title='Delete' placement='bottom-start'>
          <TrashIcon />
        </Tooltip>
      </Button>
    </ButtonsWrapper>
  )
}

export default RiskControlButtons
